import { NavLink, Outlet, useNavigate, useOutletContext } from "react-router-dom";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { LogOut, ShoppingCart, ShieldCheck, History, Circle, AlertTriangle, Printer, Smartphone, Umbrella, BarChart3, Lock, Clock } from "lucide-react";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { formatBs } from "@/lib/format";
import { ConnectLogo } from "@/components/brand/ConnectLogo";
import ThemeToggle from "@/components/brand/ThemeToggle";
import { PrinterSettingsDialog } from "@/components/pos/PrinterSettingsDialog";
import PosReportsModal from "@/components/pos/PosReportsModal";

const NAV = [
  { to: "/pos", label: "Vender", icon: ShoppingCart, testid: "pos-nav-sell", end: true, transactional: true },
  { to: "/pos/recargas", label: "Recargas", icon: Smartphone, testid: "pos-nav-recharges", transactional: true },
  { to: "/pos/seguros", label: "Seguros", icon: Umbrella, testid: "pos-nav-seguros", transactional: true },
  { to: "/pos/validar", label: "Validar", icon: ShieldCheck, testid: "pos-nav-validate" },
  { to: "/pos/historial", label: "Historial de ticket", icon: History, testid: "pos-nav-history" },
];

// Umbral (min) tras el cual mostramos aviso "desactualizada" en el badge de tasa.
const RATE_STALE_MIN = 90;

function relativeMinutes(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.round((Date.now() - t) / 60000));
}

function formatAgo(mins) {
  if (mins == null) return "sin datos";
  if (mins < 1) return "hace segundos";
  if (mins < 60) return `hace ${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `hace ${h}h ${m}m` : `hace ${h}h`;
}

const fmtSecs = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

const REASON_LABEL = { taquilla: "esta taquilla", agencia: "la agencia", grupo: "el grupo", banca: "la banca" };

// Overlay de bloqueo transaccional (venta/recargas/seguros). El contenedor padre debe ser `relative`.
export function PosLockedOverlay() {
  const gate = useOutletContext() || {};
  if (!gate.suspended) return null;
  const st = gate.taqStatus || {};
  const stateLabel = st.state === "inactivo" ? "inactiva" : "suspendida";
  return (
    <div className="absolute inset-0 z-30 min-h-[60vh] bg-rose-950/70 backdrop-blur-[2px] flex items-start justify-center overflow-y-auto p-4" data-testid="pos-suspended-overlay">
      <Card className="w-full max-w-xl mt-6 md:mt-12 bg-rose-500/15 border-rose-500/50 p-6 text-center" data-testid="pos-suspended-banner">
        <Lock size={30} className="mx-auto text-rose-300 mb-2" />
        <div className="text-xl font-bold text-rose-100">Taquilla Suspendida por Administración</div>
        <div className="text-sm font-semibold text-rose-200 mt-1">Emisión y Pagos Inhabilitados</div>
        <div className="text-xs text-rose-200/80 mt-2" data-testid="pos-suspended-reason">
          Bloqueo: {REASON_LABEL[st.suspended_reason] || "la taquilla"} está {stateLabel}. La venta, recargas, seguros y pago de tickets están bloqueados.
        </div>
        {gate.consultActive ? (
          <div className="mt-4 flex flex-col items-center gap-2">
            <div className="text-xs text-rose-100">Ventana de consulta de cuadre activa: <span className="font-mono font-bold tabular-nums" data-testid="pos-overlay-countdown">{fmtSecs(gate.consultSecs)}</span></div>
            <Button onClick={gate.openReports} data-testid="pos-overlay-open-reports"><BarChart3 size={16} className="mr-1.5" /> Abrir Reportes / Cuadre</Button>
          </div>
        ) : (
          <div className="text-xs text-rose-200/70 mt-3">Contacte a su administrador (Banca) para habilitar una consulta de cuadre.</div>
        )}
      </Card>
    </div>
  );
}

export default function PosShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [printerOpen, setPrinterOpen] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);
  const isOperator = !!user && (user.role === "vendedor" || user.role === "taquilla_operator");

  const { data: taqStatus } = useQuery({
    queryKey: ["taquilla-status", user?.id],
    queryFn: async () => (await api.get("/taquillas/me/status")).data,
    enabled: isOperator,
    refetchInterval: 30000,
    refetchOnWindowFocus: "always",
  });

  const [now, setNow] = useState(Date.now());
  const consultUntil = taqStatus?.consult_active ? taqStatus?.consult_until : null;
  useEffect(() => {
    if (!consultUntil) return undefined;
    setNow(Date.now());
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [consultUntil]);
  const suspended = !!taqStatus?.suspended;
  const consultSecs = consultUntil ? Math.max(0, Math.round((new Date(consultUntil).getTime() - now) / 1000)) : 0;
  const consultActive = suspended && consultSecs > 0;
  const reportsLocked = suspended && !consultActive;
  useEffect(() => { if (reportsLocked) setReportsOpen(false); }, [reportsLocked]);
  const gate = { suspended, consultActive, consultSecs, taqStatus, openReports: () => setReportsOpen(true) };

  const { data: session } = useQuery({
    queryKey: ["cash-session", user?.id],
    queryFn: async () => (await api.get("/cash-sessions/current")).data,
    refetchInterval: 15000,
    enabled: !!user && (user.role === "vendedor" || user.role === "taquilla_operator"),
  });

  const { data: rate } = useQuery({
    queryKey: ["exchange-rate-current"],
    queryFn: async () => (await api.get("/exchange-rates/current")).data,
    refetchInterval: 60000,
  });

  const { data: syncStatus } = useQuery({
    queryKey: ["exchange-rate-sync-status"],
    queryFn: async () => (await api.get("/exchange-rates/sync/status")).data,
    refetchInterval: 60000,
  });

  const syncMins = relativeMinutes(syncStatus?.at);
  const stale = (syncStatus?.ok === false) || (syncMins != null && syncMins > RATE_STALE_MIN);

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header
        data-testid="pos-topbar"
        className="sticky top-0 z-20 bg-card border-b border-border"
      >
        <div className="px-4 sm:px-6 py-3 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <ConnectLogo height={32} className="rounded-md" />
            <div className="hidden sm:block h-8 w-px bg-border" />
            <div className="hidden sm:block">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">POS Taquilla</div>
              <div className="text-sm font-medium leading-tight">{user?.full_name}</div>
            </div>
          </div>

          <nav className="flex items-center gap-1 md:gap-2 ml-2">
            {NAV.map((n) => {
              const Icon = n.icon;
              if (n.transactional && suspended) {
                return (
                  <button
                    key={n.to}
                    type="button"
                    disabled
                    aria-disabled="true"
                    title="Bloqueado: taquilla suspendida"
                    data-testid={n.testid}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border border-border text-muted-foreground opacity-50 cursor-not-allowed"
                  >
                    <Icon size={16} strokeWidth={1.75} />
                    <span className="hidden sm:inline">{n.label}</span>
                  </button>
                );
              }
              return (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  data-testid={n.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border ${
                      isActive ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"
                    }`
                  }
                >
                  <Icon size={16} strokeWidth={1.75} />
                  <span className="hidden sm:inline">{n.label}</span>
                </NavLink>
              );
            })}
            <button
              onClick={() => setReportsOpen(true)}
              disabled={reportsLocked}
              title={reportsLocked ? "Bloqueado: taquilla suspendida" : "Reportes"}
              data-testid="pos-nav-reports"
              className="flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border border-border text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-muted-foreground"
            >
              <BarChart3 size={16} strokeWidth={1.75} />
              <span className="hidden sm:inline">Reportes</span>
            </button>
          </nav>

          <div className="ml-auto flex items-center gap-2 flex-wrap">
            {rate?.bs_per_usd && (
              <TooltipProvider delayDuration={100}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge
                      variant="outline"
                      className={`gap-1.5 cursor-help ${
                        stale
                          ? "border-amber-500/50 text-amber-300 bg-amber-500/10"
                          : "border-primary/40 text-primary"
                      }`}
                      data-testid="pos-bcv-rate"
                    >
                      {stale && <AlertTriangle size={12} className="text-amber-300" />}
                      <span className="text-xs uppercase tracking-widest text-muted-foreground">BCV</span>
                      <span className="font-mono tabular-nums">{formatBs(rate.bs_per_usd)}</span>
                      <span className="hidden md:inline text-[10px] text-muted-foreground/80 ml-1" data-testid="pos-bcv-sync-ago">
                        {formatAgo(syncMins)}
                      </span>
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-[260px]">
                    <div className="space-y-1 text-xs">
                      <div><span className="text-muted-foreground">Tasa vigente:</span> <span className="font-mono">{formatBs(rate.bs_per_usd)} Bs/USD</span></div>
                      <div><span className="text-muted-foreground">Fuente:</span> {rate.source || "—"}</div>
                      <div><span className="text-muted-foreground">Última sync:</span> {formatAgo(syncMins)}</div>
                      {syncStatus?.fuente && (
                        <div><span className="text-muted-foreground">Proveedor:</span> {syncStatus.fuente}</div>
                      )}
                      {stale && (
                        <div className="text-amber-300 border-t border-border pt-1 mt-1">
                          {syncStatus?.ok === false
                            ? `Última sync falló: ${syncStatus?.error || "error"}`
                            : "La tasa no se ha refrescado recientemente."}
                        </div>
                      )}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {(user?.role === "vendedor" || user?.role === "taquilla_operator") && (
              <Badge
                data-testid="pos-cash-session-status"
                className={`gap-1.5 ${session ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30" : "bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/30"}`}
              >
                <Circle size={8} className={session ? "fill-emerald-400 text-emerald-400" : "fill-amber-400 text-amber-400"} />
                {session ? "Caja Abierta" : "Caja Cerrada"}
              </Badge>
            )}

            <ThemeToggle />

            <Button
              variant="outline"
              size="sm"
              onClick={() => setPrinterOpen(true)}
              data-testid="pos-printer-settings-button"
              className="text-muted-foreground"
              title="Configurar impresora"
            >
              <Printer size={16} className="sm:mr-1.5" /><span className="hidden sm:inline">Impresora</span>
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={doLogout}
              data-testid="pos-logout-button"
              className="text-muted-foreground"
            >
              <LogOut size={16} className="mr-1.5" /> Salir
            </Button>
          </div>
        </div>
      </header>
      <PrinterSettingsDialog open={printerOpen} onClose={() => setPrinterOpen(false)} operatorName={user?.full_name || ""} />
      <PosReportsModal open={reportsOpen && !reportsLocked} onClose={() => setReportsOpen(false)} />

      {suspended && (
        <div className={`px-4 sm:px-6 py-2 flex items-center gap-3 flex-wrap text-sm border-b ${consultActive ? "bg-amber-500/15 border-amber-500/40 text-amber-800 dark:text-amber-200" : "bg-rose-500/15 border-rose-500/40 text-rose-800 dark:text-rose-200"}`} data-testid="pos-suspension-banner">
          <Lock size={15} />
          <span className="font-semibold">Taquilla Suspendida por Administración — Emisión y Pagos Inhabilitados</span>
          {consultActive && (
            <span className="ml-auto inline-flex items-center gap-2" data-testid="pos-consult-banner">
              <Clock size={14} /> Consulta de reportes habilitada · restan
              <span className="font-mono font-bold tabular-nums" data-testid="pos-consult-countdown">{fmtSecs(consultSecs)}</span>
            </span>
          )}
        </div>
      )}

      <main className="flex-1 min-w-0">
        <Outlet context={gate} />
      </main>
    </div>
  );
}
